import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useFilter } from '@/lib/contexts/FilterContext'
import { useSiteSafe } from '@/lib/contexts/SiteContext'
import { useWorkspaceSafe } from '@/lib/contexts/WorkspaceContext'
import {
  getAggregatedTrafficMetrics,
} from '@/lib/api/bridge'
import {
  getHeatmapPages,
  addHeatmapPage,
  removeHeatmapPage,
  captureHeatmapScreenshot,
  getHeatmapData,
  getSavedFunnels,
  saveFunnel,
  updateFunnel,
  deleteFunnel
} from '@/lib/api/api'
import { interceptDemoAPI, demoGenerators, demoFunnelStore } from '@/lib/utils/demoData'
import { isDemoSite } from '@/lib/utils/demoDataGenerator'
import { transformFromLegacy } from '@/lib/api/bridge/transformers'

export function useAnalyticsData() {
  const { token } = useAuth()
  const { filters, period } = useFilter()
  const siteContext = useSiteSafe()
  const workspaceContext = useWorkspaceSafe()
  const fallbackPropertyId = siteContext?.currentSite?.propertyId || siteContext?.id
  const propertyIds = workspaceContext?.selectedPropertyIds?.length
    ? workspaceContext.selectedPropertyIds
    : fallbackPropertyId ? [fallbackPropertyId] : []

  return useQuery({
    queryKey: ['analytics', propertyIds.join(','), period, filters],
    queryFn: () => {
      if (!token || !propertyIds.length) throw new Error('Token and propertyIds required')
      const filterClauses = transformFromLegacy.filters(filters)
      return interceptDemoAPI(
        propertyIds[0],
        () => getAggregatedTrafficMetrics(propertyIds, period, filterClauses, token),
        demoGenerators.dashboard
      )
    },
    enabled: !!token && propertyIds.length > 0,
    staleTime: 60 * 1000,
  })
}

// Helper to get propertyId from context or options
function usePropertyId(optionsPropertyId?: string) {
  const siteContext = useSiteSafe()
  const workspaceContext = useWorkspaceSafe()

  return (
    optionsPropertyId ||
    (workspaceContext ? workspaceContext.selectedPropertyId : null) ||
    siteContext?.currentSite?.propertyId ||
    siteContext?.id
  )
}

export function useHeatmapPages(options?: { propertyId?: string; token?: string }) {
  const { token } = useAuth()
  const authToken = options?.token || token
  const propertyId = usePropertyId(options?.propertyId)
  
  return useQuery({
    queryKey: ['heatmap-pages', propertyId],
    queryFn: () => {
      if (!authToken || !propertyId) throw new Error('Token and propertyId required')
      return getHeatmapPages(propertyId, authToken)
    },
    enabled: !!authToken && !!propertyId,
  })
}

export function useAddHeatmapPage(options?: { propertyId?: string }) {
  const { token } = useAuth()
  const propertyId = usePropertyId(options?.propertyId)
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ pageUrl, title }: { pageUrl: string; title: string | null }) => {
      if (!token || !propertyId) throw new Error('Token and propertyId required')
      return addHeatmapPage(propertyId, pageUrl, title, token)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['heatmap-pages', propertyId] })
    },
  })
}

export function useRemoveHeatmapPage(options?: { propertyId?: string }) {
  const { token } = useAuth()
  const propertyId = usePropertyId(options?.propertyId)
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (pageUrl: string) => {
      if (!token || !propertyId) throw new Error('Token and propertyId required')
      return removeHeatmapPage(propertyId, pageUrl, token)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['heatmap-pages', propertyId] })
    },
  })
}

export function useCaptureScreenshot(options?: { propertyId?: string }) {
  const { token } = useAuth()
  const propertyId = usePropertyId(options?.propertyId)
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ pageUrl, deviceType }: { pageUrl: string; deviceType: 'mobile' | 'desktop' | null }) => {
      if (!token || !propertyId) throw new Error('Token and propertyId required')
      return captureHeatmapScreenshot(propertyId, pageUrl, deviceType, token)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['heatmap-pages', propertyId] })
    },
  })
}

export function useHeatmapData(pageUrl: string, options?: { propertyId?: string; token?: string }) {
  const { token } = useAuth()
  const authToken = options?.token || token
  const { period } = useFilter()
  const propertyId = usePropertyId(options?.propertyId)
  
  // Check if it's a custom date range (contains ':') - legacy API doesn't support this
  const isCustomDateRange = period.includes(':')
  
  return useQuery({
    queryKey: ['heatmap-data', propertyId, pageUrl, period],
    queryFn: () => {
      if (!authToken || !propertyId) throw new Error('Token and propertyId required')
      // Custom date ranges not supported by legacy heatmap API
      if (isCustomDateRange) {
        return { isCustomDateRange: true, points: [], scrollDepth: null }
      }
      return getHeatmapData(period, pageUrl, authToken, 'America/Detroit', propertyId)
    },
    enabled: !!authToken && !!propertyId && !!pageUrl,
    staleTime: 60 * 1000, // 1 minute
  })
}

export function useSavedFunnels(options?: { propertyId?: string }) {
  const { token } = useAuth()
  const propertyId = usePropertyId(options?.propertyId)
  const workspaceContext = useWorkspaceSafe()

  const isAggregateMode = !!workspaceContext?.isAggregateMode
  const aggregatePropertyIds = workspaceContext?.aggregatePropertyIds || []
  const enabledProperties = workspaceContext?.enabledProperties || []

  return useQuery({
    queryKey: ['saved-funnels', isAggregateMode ? aggregatePropertyIds.join(',') : propertyId],
    queryFn: async () => {
      if (!token) throw new Error('Token required')
      if (isAggregateMode) {
        const selectedProps = aggregatePropertyIds
          .map(id => enabledProperties.find(p => p.propertyId === id))
          .filter(Boolean) as typeof enabledProperties
        const results = await Promise.all(
          selectedProps.map(async (prop) => {
            try {
              const funnels = await getSavedFunnels(prop.propertyId, token)
              return Array.isArray(funnels)
                ? funnels.map((f: any) => ({
                    ...f,
                    _propertyId: prop.propertyId,
                    _propertyName: prop.name,
                    _propertyDomain: prop.domain,
                  }))
                : []
            } catch {
              return []
            }
          })
        )
        return results.flat()
      }
      if (!propertyId) throw new Error('propertyId required')
      return interceptDemoAPI(
        propertyId,
        () => getSavedFunnels(propertyId, token),
        demoGenerators.funnels
      )
    },
    enabled: !!token && (isAggregateMode ? aggregatePropertyIds.length > 0 : !!propertyId),
  })
}

export function useSaveFunnel(options?: { propertyId?: string }) {
  const { token } = useAuth()
  const propertyId = usePropertyId(options?.propertyId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (funnelData: any) => {
      if (!token) throw new Error('Authentication required')
      if (!propertyId) throw new Error('Please select a site to save the funnel')
      if (isDemoSite(propertyId)) {
        return Promise.resolve(demoFunnelStore.create(funnelData))
      }
      return saveFunnel(funnelData, propertyId, token)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-funnels', propertyId] })
    },
  })
}

export function useUpdateFunnel(options?: { propertyId?: string }) {
  const { token } = useAuth()
  const propertyId = usePropertyId(options?.propertyId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ funnelId, funnelData }: { funnelId: string; funnelData: any }) => {
      if (!token) throw new Error('Authentication required')
      if (!propertyId) throw new Error('Please select a site to save the funnel')
      if (isDemoSite(propertyId)) {
        return Promise.resolve(demoFunnelStore.update(funnelId, funnelData))
      }
      return updateFunnel(funnelData, propertyId, funnelId, token)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-funnels', propertyId] })
    },
  })
}

export function useDeleteFunnel(options?: { propertyId?: string }) {
  const { token } = useAuth()
  const propertyId = usePropertyId(options?.propertyId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (funnelId: string) => {
      if (!token) throw new Error('Authentication required')
      if (!propertyId) throw new Error('Please select a site to delete the funnel')
      if (isDemoSite(propertyId)) {
        demoFunnelStore.delete(funnelId)
        return Promise.resolve(null)
      }
      return deleteFunnel(propertyId, funnelId, token)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-funnels', propertyId] })
    },
  })
}
